import React from 'react';

import { useCliveInsightsViewModel } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useCliveInsightsViewModel';
import { ActionsV2 } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/ActionsV2';
import { InsightCardV2 } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/InsightCardV2';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { Heading, Text } from '~/components/core/TextComponents';

/**
 * Content of the Clive Insights tab: the insight cards and the recommended actions,
 * both read from useCliveInsightsViewModel.
 */
export const CliveInsightsContentV2: React.FC = () => {
  const {
    insights,
    references,
    isInsightsEnabled,
    isInsightsLoading,
    hasInsights,
    filteredActions,
    isActionsLoading,
    hasActions,
    reloadActions,
    areActionsInConductorHub,
    isLoading,
    hasNoData,
  } = useCliveInsightsViewModel();

  if (!isLoading && hasNoData) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-white">
        <div className="flex flex-col items-center text-center">
          <CliveAvatar mood={CliveAvatar.MOOD.SAD} className="mb-16" size={100} />
          <Heading variant={Heading.TYPES.H2} className="mb-8">
            {areActionsInConductorHub ? 'No insights yet' : 'No insights or actions yet'}
          </Heading>
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {areActionsInConductorHub
              ? 'Clive will analyze your claim and provide insights here.'
              : 'Clive will analyze your claim and provide insights and recommended actions here.'}
          </Text>
        </div>
      </div>
    );
  }

  return (
    // scrollbar-gutter keeps the scrollbar's space reserved whether or not it is showing. Without
    // it, expanding a card crosses the overflow threshold and the scrollbar appears, taking its
    // width out of the content box — every card narrows and its contents reflow, so opening one
    // card visibly shifts all the others.
    <div className="flex h-full flex-col gap-24 overflow-auto rounded bg-white px-24 py-20 [scrollbar-gutter:stable]">
      <header className="flex items-center gap-12">
        <CliveAvatar mood={CliveAvatar.MOOD.HAPPY} size={40} className="shrink-0" />
        <Heading variant={Heading.TYPES.H2}>Clive Insights</Heading>
      </header>

      {isInsightsEnabled && (hasInsights || isInsightsLoading) ? (
        <section className="flex flex-col gap-16">
          {isInsightsLoading ? (
            <SkeletonTable rowsCount={5} columnsCount={1} />
          ) : (
            <ul className="m-0 flex w-full flex-col gap-16 p-0">
              {insights.map((insight) => (
                <InsightCardV2 insight={insight} references={references} key={insight.id} />
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {hasActions || isActionsLoading ? (
        <section className="mt-4 flex flex-col gap-16">
          <ActionsV2 isLoading={isActionsLoading} actions={filteredActions} reloadActions={reloadActions} />
        </section>
      ) : null}
    </div>
  );
};
